import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  Slider,
  Select,
  MenuItem,

  Tab,
  Tabs,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';
import { preferencesService, type DashboardPreferences } from '@services/preferences.service';

interface DashboardPreferencesProps {
  open: boolean;
  onClose: () => void;
  preferences: DashboardPreferences;
  onPreferencesChange: (preferences: DashboardPreferences) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`preferences-tabpanel-${index}`}
      aria-labelledby={`preferences-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const DashboardPreferences: React.FC<DashboardPreferencesProps> = ({
  open,
  onClose,
  preferences,
  onPreferencesChange,
}) => {
  const [localPreferences, setLocalPreferences] = useState<DashboardPreferences>(preferences);
  const [activeTab, setActiveTab] = useState(0);

  const handleChange = (section: keyof DashboardPreferences, key: string, value: any) => {
    setLocalPreferences(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value,
      },
    }));
  };

  const handleSave = () => {
    preferencesService.savePreferences(localPreferences);
    onPreferencesChange(localPreferences);
    onClose();
  };

  const handleReset = () => {
    preferencesService.resetPreferences();
    const defaultPrefs = preferencesService.getPreferences();
    setLocalPreferences(defaultPrefs);
    onPreferencesChange(defaultPrefs);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Dashboard Preferences</Typography>
          <IconButton onClick={onClose} size="small" aria-label="Close dashboard preferences">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Layout" />
            <Tab label="Display" />
            <Tab label="Export" />
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend">Metrics Position</FormLabel>
            <RadioGroup
              value={localPreferences.layout.metricsPosition}
              onChange={(e) => handleChange('layout', 'metricsPosition', e.target.value)}
            >
              <FormControlLabel value="top" control={<Radio />} label="Top" />
              <FormControlLabel value="bottom" control={<Radio />} label="Bottom" />
            </RadioGroup>
          </FormControl>

          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend">Default Chart Type</FormLabel>
            <RadioGroup
              value={localPreferences.layout.chartType}
              onChange={(e) => handleChange('layout', 'chartType', e.target.value)}
            >
              <FormControlLabel value="area" control={<Radio />} label="Area Chart" />
              <FormControlLabel value="line" control={<Radio />} label="Line Chart" />
              <FormControlLabel value="bar" control={<Radio />} label="Bar Chart" />
            </RadioGroup>
          </FormControl>

          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend">Activity Feed Position</FormLabel>
            <RadioGroup
              value={localPreferences.layout.activityFeedPosition}
              onChange={(e) => handleChange('layout', 'activityFeedPosition', e.target.value)}
            >
              <FormControlLabel value="right" control={<Radio />} label="Right" />
              <FormControlLabel value="bottom" control={<Radio />} label="Bottom" />
            </RadioGroup>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={localPreferences.layout.showConnectionStatus}
                onChange={(e) => handleChange('layout', 'showConnectionStatus', e.target.checked)}
              />
            }
            label="Show Connection Status"
            sx={{ mb: 2 }}
          />

          <Box sx={{ mb: 3 }}>
            <Typography gutterBottom>Refresh Interval (seconds)</Typography>
            <Slider
              value={localPreferences.layout.refreshInterval}
              onChange={(_, value) => handleChange('layout', 'refreshInterval', value)}
              min={5}
              max={300}
              step={5}
              marks={[
                { value: 5, label: '5s' },
                { value: 60, label: '1m' },
                { value: 300, label: '5m' },
              ]}
              valueLabelDisplay="auto"
            />
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <FormControlLabel
            control={
              <Switch
                checked={localPreferences.display.compactMode}
                onChange={(e) => handleChange('display', 'compactMode', e.target.checked)}
              />
            }
            label="Compact Mode"
            sx={{ mb: 2 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={localPreferences.display.animationsEnabled}
                onChange={(e) => handleChange('display', 'animationsEnabled', e.target.checked)}
              />
            }
            label="Enable Animations"
            sx={{ mb: 2 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={localPreferences.display.showTrends}
                onChange={(e) => handleChange('display', 'showTrends', e.target.checked)}
              />
            }
            label="Show Trends"
            sx={{ mb: 3 }}
          />

          <Box sx={{ mb: 3 }}>
            <Typography gutterBottom>Activity Feed Limit</Typography>
            <Slider
              value={localPreferences.display.activityLimit}
              onChange={(_, value) => handleChange('display', 'activityLimit', value)}
              min={10}
              max={100}
              step={10}
              marks={[
                { value: 10, label: '10' },
                { value: 50, label: '50' },
                { value: 100, label: '100' },
              ]}
              valueLabelDisplay="auto"
            />
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <FormLabel>Default Export Format</FormLabel>
            <Select
              value={localPreferences.export.defaultFormat}
              onChange={(e) => handleChange('export', 'defaultFormat', e.target.value)}
            >
              <MenuItem value="csv">CSV</MenuItem>
              <MenuItem value="json">JSON</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={localPreferences.export.includeMetadata}
                onChange={(e) => handleChange('export', 'includeMetadata', e.target.checked)}
              />
            }
            label="Include Metadata in Exports"
          />
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button
          startIcon={<ResetIcon />}
          onClick={handleReset}
          color="inherit"
        >
          Reset to Defaults
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save Preferences
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DashboardPreferences;
